// src/modules/units/tyres/shared/tyre-import-file.js
//
// Spreadsheet readers for the tyre importer: .xlsx and .csv/.txt into a plain
// matrix of cell values. No DOM (no DOMParser), so every function here runs in the
// Node harness as well as the browser.
//
// WHY NOT SheetJS. The `xlsx` package on npm is frozen at 0.18.5, which carries
// published advisories (prototype pollution, ReDoS); the maintained builds are only
// distributed from the vendor's own CDN, outside npm's audit trail. An .xlsx is a
// zip of XML parts, and the project already depends on jszip, so a focused reader
// for the parts an import needs (workbook, rels, shared strings, styles for date
// detection, sheet cells) is ~250 lines we own and can test — and jszip is loaded
// lazily, so the import screen costs the main bundle nothing.
//
// Scope, stated plainly: this reads VALUES. Formulas contribute their cached
// result (what Excel last calculated and saved). Legacy binary .xls (Excel 97-2003)
// is not supported; readSpreadsheet() says so, and the user re-saves as .xlsx or CSV.
//
// Result shape (both formats):
//   { sheets: [{ name, rows: Array<Array<string|number|boolean|null>>, truncated,
//                suggested?: Set<"row:col"> }] }   (suggested: .xlsx only — cells
//                computed by the import template's catalogue-suggestion formulas)
// Date-formatted numeric cells are converted to 'YYYY-MM-DD' strings here, because
// only the workbook's styles know a number is a date; everything downstream sees a
// plain value.

import { excelSerialToYmd } from './tyre-import-values.js';

export const MAX_IMPORT_ROWS = 5000;

// The import template's hidden lookup sheet. A cell whose formula reads from it
// holds a CATALOGUE SUGGESTION (the template's auto-fill), not a value someone
// typed — the importer treats those like the form treats its auto-filled fields
// (see tyre-import-validate.js keepExistingOverSuggestions). The name is
// deliberately distinctive so a customer's own "Lists" sheet is never mistaken
// for it.
export const SUGGESTION_SHEET = 'PT_Lists';
const SUGGESTION_REF = new RegExp(`(^|[^A-Za-z0-9_.'])'?${SUGGESTION_SHEET}'?!`, 'i');

// A formula with its "string literals" blanked: text in quotes is data, not a reference.
const formulaCode = (text) => String(text || '').replace(/"(?:[^"]|"")*"/g, '""');
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Does this formula read from the template's lookup sheet — directly
 * (PT_Lists!$N$2:…) or through a workbook name defined on it? The template uses
 * names (Excel forbids a shared formula from referring to another sheet), so pass
 * the workbook's suggestion names (see suggestionNamesOf).
 * @param {string} text formula, no leading '='
 * @param {Iterable<string>} [names] defined names that refer to the lookup sheet
 */
export function isSuggestionFormula(text, names = []) {
  const code = formulaCode(text);
  if (SUGGESTION_REF.test(code)) return true;
  for (const n of names) {
    // A whole name token: not part of a longer name, a sheet-qualified ref or a call.
    if (new RegExp(`(^|[^A-Za-z0-9_.!\\\\])${escapeRe(n)}(?![A-Za-z0-9_.(!])`, 'i').test(code)) return true;
  }
  return false;
}

/** Names in a workbook.xml whose definition reads the lookup sheet. */
export function suggestionNamesOf(workbookXml) {
  const out = [];
  const re = /<definedName\b([^>]*)>([\s\S]*?)<\/definedName>/g;
  let m;
  while ((m = re.exec(String(workbookXml || '')))) {
    const a = attrs(m[1]);
    if (a.name && SUGGESTION_REF.test(formulaCode(decodeXml(m[2])))) out.push(a.name);
  }
  return out;
}
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Tiny XML helpers (the OOXML parts are machine-written and regular)
// ---------------------------------------------------------------------------

const ENTITIES = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };

export function decodeXml(s) {
  if (!s) return '';
  return String(s)
    .replace(/&(#x[0-9a-fA-F]+|#\d+|lt|gt|amp|quot|apos);/g, (_, e) => {
      if (e[0] !== '#') return ENTITIES[e];
      const code = e[1] === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : '';
    })
    // OOXML escapes control characters as _xHHHH_ (e.g. _x000D_ for CR).
    .replace(/_x([0-9A-Fa-f]{4})_/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
}

// Drop namespace prefixes from element names (<x:c> → <c>) so one set of patterns
// reads files from Excel, LibreOffice, Google Sheets and server-side generators.
const stripPrefixes = (xml) => String(xml || '').replace(/<(\/?)[A-Za-z_][\w.-]*:/g, '<$1');

function attrs(tagBody) {
  const out = {};
  const re = /([\w:.-]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
  let m;
  while ((m = re.exec(tagBody))) out[m[1]] = decodeXml(m[3] ?? m[4] ?? '');
  return out;
}

// Concatenate the <t> runs of a string item, ignoring phonetic (<rPh>) runs.
function textOf(xml) {
  const body = String(xml || '').replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
  let out = '';
  const re = /<t\b[^>]*>([\s\S]*?)<\/t>|<t\b[^>]*\/>/g;
  let m;
  while ((m = re.exec(body))) out += decodeXml(m[1] || '');
  return out;
}

// "BC12" → 54 (0-based column index)
export function columnIndex(ref) {
  const letters = String(ref || '').match(/^[A-Za-z]+/);
  if (!letters) return -1;
  let n = 0;
  for (const ch of letters[0].toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

// ---------------------------------------------------------------------------
// Date-format detection from styles.xml
// ---------------------------------------------------------------------------

// Built-in number formats that are dates/times (ECMA-376 §18.8.30), plus the
// locale-specific ids Excel uses for CJK date formats.
const BUILTIN_DATE_FORMATS = new Set([
  14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36,
  45, 46, 47, 50, 51, 52, 53, 54, 55, 56, 57, 58
]);

export function isDateFormatCode(code) {
  if (!code) return false;
  // Remove quoted literals, escaped chars and [colour]/[locale] blocks, then look
  // for date/time tokens. "General" and pure number formats have none.
  const bare = String(code)
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '')
    .replace(/\[[^\]]*\]/g, '');
  if (/general/i.test(bare)) return false;
  return /[dmyhs]/i.test(bare);
}

function readDateStyles(stylesXml) {
  const xml = stripPrefixes(stylesXml);
  const custom = new Map();
  const fmtRe = /<numFmt\b([^>]*)\/?>/g;
  let m;
  while ((m = fmtRe.exec(xml))) {
    const a = attrs(m[1]);
    custom.set(Number(a.numFmtId), a.formatCode || '');
  }
  const xfsBlock = (xml.match(/<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/) || [])[1] || '';
  const dateStyles = new Set();
  const xfRe = /<xf\b([^>]*?)\/?>/g;
  let i = 0;
  while ((m = xfRe.exec(xfsBlock))) {
    const id = Number(attrs(m[1]).numFmtId || 0);
    if (BUILTIN_DATE_FORMATS.has(id) || (custom.has(id) && isDateFormatCode(custom.get(id)))) {
      dateStyles.add(i);
    }
    i++;
  }
  return dateStyles;
}

// ---------------------------------------------------------------------------
// XLSX
// ---------------------------------------------------------------------------

function resolveTarget(target) {
  const t = String(target || '').replace(/\\/g, '/');
  if (t.startsWith('/')) return t.slice(1);
  // Relative to xl/; collapse any ../ segments.
  const parts = ['xl', ...t.split('/')];
  const out = [];
  for (const p of parts) {
    if (p === '..') out.pop();
    else if (p && p !== '.') out.push(p);
  }
  return out.join('/');
}

function readSharedStrings(xml) {
  const body = stripPrefixes(xml);
  const out = [];
  const re = /<si\b[^>]*>([\s\S]*?)<\/si>|<si\b[^>]*\/>/g;
  let m;
  while ((m = re.exec(body))) out.push(textOf(m[1] || ''));
  return out;
}

function readSheetRows(sheetXml, { sharedStrings, dateStyles, date1904, maxRows, suggestionNames = [] }) {
  const xml = stripPrefixes(sheetXml);
  const data = (xml.match(/<sheetData\b[^>]*>([\s\S]*?)<\/sheetData>/) || [])[1] || '';
  const rows = [];
  const rowRe = /<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g;
  let rm;
  let nextRow = 0;
  let truncated = false;
  // Cells whose value came from a template suggestion formula, as "row:col".
  // A shared formula's text lives only on its first cell; the rest carry its si.
  const suggested = new Set();
  const sharedText = new Map();

  while ((rm = rowRe.exec(data))) {
    const ra = attrs(rm[1]);
    const r = ra.r ? Number(ra.r) - 1 : nextRow;
    nextRow = r + 1;
    if (r >= maxRows + 1) { truncated = true; break; } // +1 leaves room for a header row
    const cells = [];
    const cellRe = /<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;
    let cm;
    let nextCol = 0;
    while ((cm = cellRe.exec(rm[2] || ''))) {
      const ca = attrs(cm[1]);
      const col = ca.r ? columnIndex(ca.r) : nextCol;
      nextCol = col + 1;
      const inner = cm[2] || '';
      const vRaw = (inner.match(/<v\b[^>]*>([\s\S]*?)<\/v>/) || [])[1];
      const fm = inner.match(/<f\b([^>]*?)(?:\/>|>([\s\S]*?)<\/f>)/);
      let formula = '';
      if (fm) {
        const fa = attrs(fm[1]);
        formula = decodeXml(fm[2] || '');
        if (fa.t === 'shared' && fa.si !== undefined) {
          if (formula) sharedText.set(fa.si, formula);
          else formula = sharedText.get(fa.si) || '';
        }
      }
      const t = ca.t || 'n';
      let value = null;

      if (t === 's') {
        const idx = Number(vRaw);
        value = Number.isInteger(idx) ? (sharedStrings[idx] ?? '') : '';
      } else if (t === 'inlineStr') {
        value = textOf((inner.match(/<is\b[^>]*>([\s\S]*?)<\/is>/) || [])[1] || '');
      } else if (t === 'str') {
        value = decodeXml(vRaw ?? '');
      } else if (t === 'b') {
        value = vRaw === '1';
      } else if (t === 'e') {
        value = null; // #N/A, #REF! … — an error cell carries no usable value
      } else if (t === 'd') {
        value = vRaw ? String(decodeXml(vRaw)).slice(0, 10) : null;
      } else if (vRaw !== undefined && vRaw !== '') {
        const n = Number(vRaw);
        if (!Number.isFinite(n)) value = decodeXml(vRaw);
        else if (ca.s !== undefined && dateStyles.has(Number(ca.s))) value = excelSerialToYmd(n, { date1904 }) ?? n;
        else value = n;
      }

      if (value !== null && value !== '') {
        cells[col] = value;
        if (formula && isSuggestionFormula(formula, suggestionNames)) suggested.add(`${r}:${col}`);
      }
    }
    // Dense row: holes become null so column positions line up.
    for (let i = 0; i < cells.length; i++) if (cells[i] === undefined) cells[i] = null;
    rows[r] = cells;
  }
  for (let i = 0; i < rows.length; i++) if (!rows[i]) rows[i] = [];
  // Trailing rows with no values (a template's pre-formatted, formula-only rows)
  // are not data.
  while (rows.length && !rows[rows.length - 1].some((c) => c !== null && c !== undefined && c !== '')) rows.pop();
  return { rows, truncated, suggested };
}

/**
 * Read an .xlsx workbook.
 * @param {ArrayBuffer|Uint8Array} data file bytes
 * @param {object} JSZip the JSZip constructor (injected so this stays loader-agnostic)
 * @param {{maxRows?:number}} [opts]
 */
export async function readXlsx(data, JSZip, { maxRows = MAX_IMPORT_ROWS } = {}) {
  let zip;
  try {
    zip = await JSZip.loadAsync(data);
  } catch (e) {
    throw new Error('This file could not be opened as an Excel workbook (.xlsx).');
  }
  const read = async (path) => {
    const f = zip.file(path);
    return f ? f.async('string') : null;
  };

  const workbookXml = await read('xl/workbook.xml');
  if (!workbookXml) throw new Error('This file is not an Excel workbook (.xlsx).');
  const wb = stripPrefixes(workbookXml);
  const pr = attrs((wb.match(/<workbookPr\b([^>]*)\/?>/) || [])[1] || '');
  const date1904 = pr.date1904 === '1' || pr.date1904 === 'true';
  const suggestionNames = suggestionNamesOf(wb);

  const relsXml = stripPrefixes(await read('xl/_rels/workbook.xml.rels') || '');
  const rels = new Map();
  const relRe = /<Relationship\b([^>]*)\/?>/g;
  let m;
  while ((m = relRe.exec(relsXml))) {
    const a = attrs(m[1]);
    rels.set(a.Id, resolveTarget(a.Target));
  }

  const sharedStrings = readSharedStrings(await read('xl/sharedStrings.xml') || '');
  const dateStyles = readDateStyles(await read('xl/styles.xml') || '');

  const sheets = [];
  const sheetRe = /<sheet\b([^>]*)\/?>/g;
  while ((m = sheetRe.exec(wb))) {
    const a = attrs(m[1]);
    if (a.state === 'hidden' || a.state === 'veryHidden') continue;
    const path = rels.get(a['r:id'] || a.id);
    const xml = path ? await read(path) : null;
    if (!xml) continue; // chart sheets and missing parts carry no cells
    const { rows, truncated, suggested } = readSheetRows(xml, { sharedStrings, dateStyles, date1904, maxRows, suggestionNames });
    sheets.push({ name: a.name || `Sheet ${sheets.length + 1}`, rows, truncated, suggested });
  }
  if (!sheets.length) throw new Error('The workbook has no visible worksheets.');
  return { sheets };
}

// ---------------------------------------------------------------------------
// CSV (RFC 4180, with delimiter sniffing for ; and tab exports)
// ---------------------------------------------------------------------------

// Pick the delimiter from the first 20 lines, not just the first: a title line
// ("Demo Transport — tyre list") has none, and a decimal comma ("5 035,00") must
// not out-vote a semicolon layout. For each candidate, find the per-line count
// that most lines agree on; the candidate with the most agreeing lines wins (then
// the larger count, then comma as the RFC default).
export function sniffDelimiter(text) {
  const candidates = [',', ';', '\t'];
  const perLine = [];
  let counts = { ',': 0, ';': 0, '\t': 0 };
  let inQ = false;
  let any = false;
  for (let i = 0; i < text.length && perLine.length < 20; i++) {
    const ch = text[i];
    if (ch === '"') inQ = !inQ;
    else if (!inQ && (ch === '\n' || ch === '\r')) {
      if (any) perLine.push(counts);
      counts = { ',': 0, ';': 0, '\t': 0 };
      any = false;
      if (ch === '\r' && text[i + 1] === '\n') i++;
    } else if (!inQ) {
      if (ch in counts) counts[ch]++;
      if (ch.trim()) any = true;
    }
  }
  if (any && perLine.length < 20) perLine.push(counts);

  let best = ',';
  let bestScore = [0, 0];
  for (const d of candidates) {
    const freq = new Map();
    for (const c of perLine) if (c[d] > 0) freq.set(c[d], (freq.get(c[d]) || 0) + 1);
    let score = [0, 0];
    for (const [n, lines] of freq) if (lines > score[0] || (lines === score[0] && n > score[1])) score = [lines, n];
    if (score[0] > bestScore[0] || (score[0] === bestScore[0] && score[1] > bestScore[1])) { best = d; bestScore = score; }
  }
  return best;
}

/**
 * Parse CSV text into rows of strings. Handles quoted fields, escaped quotes (""),
 * embedded delimiters and newlines, CRLF/LF/CR line endings and a UTF-8 BOM.
 */
export function parseCsv(text, { delimiter, maxRows = MAX_IMPORT_ROWS } = {}) {
  const src = String(text || '').replace(/^﻿/, '');
  const d = delimiter || sniffDelimiter(src);
  const rows = [];
  let row = [];
  let field = '';
  let inQ = false;
  let truncated = false;

  const endField = () => { row.push(field); field = ''; };
  const endRow = () => {
    endField();
    rows.push(row.map((c) => (c === '' ? null : c)));
    row = [];
  };

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQ) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; } else inQ = false;
      } else field += ch;
      continue;
    }
    if (ch === '"' && field === '') inQ = true;
    else if (ch === d) endField();
    else if (ch === '\r') { if (src[i + 1] === '\n') i++; endRow(); }
    else if (ch === '\n') endRow();
    else field += ch;

    if (rows.length > maxRows) { truncated = true; break; }
  }
  if (!truncated && (field !== '' || row.length)) endRow();
  // Drop trailing blank rows (a final newline, or Excel's padding).
  while (rows.length && rows[rows.length - 1].every((c) => c === null)) rows.pop();
  return { rows, delimiter: d, truncated };
}

// ---------------------------------------------------------------------------
// File entry point (browser)
// ---------------------------------------------------------------------------

/**
 * Read a File/Blob chosen by the user into sheets. Rejects (with a user-facing
 * message) for unsupported types and oversize files.
 * @param {File} file
 * @param {{loadJSZip?: () => Promise<object>}} [opts] injectable loader for tests
 */
export async function readSpreadsheet(file, { loadJSZip } = {}) {
  const name = String(file?.name || '');
  const ext = (name.match(/\.([^.]+)$/) || [])[1]?.toLowerCase() || '';
  if (file?.size > MAX_IMPORT_BYTES) {
    throw new Error(`The file is larger than ${Math.round(MAX_IMPORT_BYTES / 1024 / 1024)} MB. Split it into smaller files.`);
  }
  if (ext === 'xls') {
    throw new Error('Old Excel 97-2003 (.xls) files are not supported. In Excel, use File → Save As → Excel Workbook (.xlsx) and choose that file.');
  }
  if (ext === 'csv' || ext === 'txt' || ext === 'tsv') {
    const { rows, truncated } = parseCsv(await file.text(), ext === 'tsv' ? { delimiter: '\t' } : {});
    return { sheets: [{ name: name || 'CSV', rows, truncated }] };
  }
  if (ext === 'xlsx' || ext === 'xlsm') {
    const JSZip = loadJSZip ? await loadJSZip() : (await import('jszip')).default;
    return readXlsx(await file.arrayBuffer(), JSZip);
  }
  throw new Error('Choose an Excel workbook (.xlsx) or a CSV file.');
}
